import React, { useRef, useState, useCallback, useEffect } from "react";
import { MoveHorizontal } from "lucide-react";

export const BeforeAfterSlider = ({ before, after, alt = "Before and after dental transformation", className = "" }) => {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(2, Math.min(98, pct)));
  }, []);

  const onDown = (e) => {
    dragging.current = true;
    setFromClientX(e.clientX ?? e.touches?.[0]?.clientX);
  };

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      const x = e.clientX ?? e.touches?.[0]?.clientX;
      if (x != null) setFromClientX(x);
    };
    const up = () => (dragging.current = false);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [setFromClientX]);

  return (
    <div
      ref={containerRef}
      data-testid="before-after-slider"
      onMouseDown={onDown}
      onTouchStart={onDown}
      className={`relative aspect-[4/5] w-full cursor-ew-resize select-none overflow-hidden rounded-2xl border border-[var(--border)] ${className}`}
    >
      {/* After (base layer, full) */}
      <img src={after} alt={`${alt} — after`} loading="lazy" className="absolute inset-0 h-full w-full object-cover" draggable={false} />
      <span className="absolute right-4 top-4 rounded-full bg-[var(--primary)] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-[var(--on-primary)]">
        After
      </span>

      {/* Before (clipped via clip-path so image never distorts) */}
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={before} alt={`${alt} — before`} loading="lazy" className="absolute inset-0 h-full w-full object-cover" draggable={false} />
        <span className="absolute left-4 top-4 rounded-full bg-black/55 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-white backdrop-blur">
          Before
        </span>
      </div>

      {/* Handle */}
      <div className="pointer-events-none absolute inset-y-0" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div className="h-full w-0.5 bg-white/90 shadow-[0_0_0_1px_rgba(0,0,0,0.06)]" />
        <div className="absolute top-1/2 left-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[var(--primary)] shadow-lg">
          <MoveHorizontal size={18} />
        </div>
      </div>
    </div>
  );
};
